import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import { ArrowLeft, Printer, Download, Calendar, Filter } from 'lucide-react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { convertArabic } from 'arabic-reshaper';
import { NotoSansArabicBase64 } from '../fonts/NotoSansArabic';

const fmt = (n) =>
  (Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const processArabicText = (text) => {
  if (!text) return '';
  const arabicRegex = /[\u0600-\u06FF]/;
  if (!arabicRegex.test(text)) return text;
  return convertArabic(text);
};

const TYPE_LABEL = {
  bill:         { en: 'Bill',          ar: 'فاتورة مورد', color: 'text-blue-700  bg-blue-50'  },
  bill_payment: { en: 'Payment',       ar: 'دفعة',        color: 'text-green-700 bg-green-50' },
  payment:      { en: 'Payment',       ar: 'دفعة',        color: 'text-green-700 bg-green-50' },
  debit_note:   { en: 'Debit Note',    ar: 'إشعار مدين',  color: 'text-amber-700 bg-amber-50' },
};

const SupplierStatement = () => {
  const { supplierId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [loading, setLoading] = useState(true);
  const [statement, setStatement] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [activeId, setActiveId] = useState(supplierId || searchParams.get('supplier') || '');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showFilter, setShowFilter] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await axiosInstance.get('/suppliers');
        setSuppliers(res.data || []);
        if (!activeId && (res.data || []).length > 0) {
          setActiveId(res.data[0].id);
        }
      } catch {
        toast.error('Failed to load suppliers list');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!activeId) {
      setLoading(false);
      setStatement(null);
      return;
    }
    (async () => {
      try {
        setLoading(true);
        const params = new URLSearchParams();
        if (dateFrom) params.append('date_from', dateFrom);
        if (dateTo) params.append('date_to', dateTo);
        const qs = params.toString();
        const url = `/accounting/supplier-statement/${activeId}${qs ? '?' + qs : ''}`;
        const res = await axiosInstance.get(url);
        setStatement(res.data);
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to load statement');
        setStatement(null);
      } finally {
        setLoading(false);
      }
    })();
  }, [activeId, dateFrom, dateTo]);

  const downloadPDF = () => {
    if (!statement) return;
    const doc = new jsPDF();
    doc.addFileToVFS('NotoSansArabic-Regular.ttf', NotoSansArabicBase64);
    doc.addFont('NotoSansArabic-Regular.ttf', 'NotoSansArabic', 'normal');

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(18);
    doc.setTextColor(13, 71, 161);
    doc.text('Supplier Statement', 105, 15, { align: 'center' });
    doc.setFont('NotoSansArabic', 'normal');
    doc.setFontSize(12);
    doc.text(processArabicText('كشف حساب مورد'), 105, 22, { align: 'center' });

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(60);
    const p = statement.party;
    const t = statement.totals;
    autoTable(doc, {
      startY: 28,
      body: [
        [`Supplier: ${p.name}`, `Code: ${p.code || '-'}`],
        [`VAT: ${p.vat_number || '-'}`, `Period: ${statement.date_from || '—'}  →  ${statement.date_to || '—'}`],
        [`Opening: ${fmt(t.opening_balance)} SAR`, `Closing: ${fmt(t.closing_balance)} SAR`],
      ],
      theme: 'plain',
      styles: { fontSize: 9, cellPadding: 1 },
      columnStyles: { 0: { cellWidth: 95 }, 1: { cellWidth: 95 } },
    });

    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 4,
      head: [['Date', 'Reference', 'Type', 'Description', 'Debit', 'Credit', 'Balance']],
      body: statement.entries.map((e) => [
        e.date, e.reference || '-', e.type, e.description || '',
        e.debit ? fmt(e.debit) : '', e.credit ? fmt(e.credit) : '', fmt(e.balance),
      ]),
      headStyles: { fillColor: [13, 71, 161], textColor: 255, fontSize: 9 },
      styles: { fontSize: 8 },
      columnStyles: { 4: { halign: 'right' }, 5: { halign: 'right' }, 6: { halign: 'right' } },
    });

    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 4,
      body: [
        ['Total Bills',        fmt(t.total_billed)      + ' SAR'],
        ['Total Payments',     fmt(t.total_paid)        + ' SAR'],
        ['Total Debit Notes',  fmt(t.total_debit_notes) + ' SAR'],
        ['Closing Balance',    fmt(t.closing_balance)   + ' SAR'],
      ],
      theme: 'striped',
      styles: { fontSize: 9 },
      columnStyles: { 0: { fontStyle: 'bold' }, 1: { halign: 'right', fontStyle: 'bold' } },
    });

    doc.save(`supplier-statement-${p.code || p.id}-${new Date().toISOString().slice(0, 10)}.pdf`);
    toast.success('PDF downloaded');
  };

  const activeSupplier = useMemo(
    () => suppliers.find((s) => s.id === activeId),
    [suppliers, activeId],
  );

  if (loading && !statement) {
    return (
      <div className="p-6 text-center text-gray-500" data-testid="supplier-statement-loading">
        Loading…
      </div>
    );
  }

  const p = statement?.party;
  const t = statement?.totals;
  const a = statement?.aging;

  return (
    <div className="p-6 space-y-6 max-w-screen-2xl mx-auto" data-testid="supplier-statement-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <button
            data-testid="supplier-statement-back"
            onClick={() => navigate('/supplier-report')}
            className="p-2 rounded-lg border bg-white hover:bg-gray-50"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-blue-700">Supplier Statement</h1>
            <p className="text-sm text-gray-500" dir="rtl">كشف حساب مورد</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <select
            data-testid="supplier-statement-selector"
            value={activeId}
            onChange={(e) => setActiveId(e.target.value)}
            className="border rounded-lg px-3 py-2 bg-white text-sm min-w-[260px]"
          >
            {suppliers.map((s) => (
              <option key={s.id} value={s.id}>
                {s.supplier_number ? `${s.supplier_number} — ` : ''}
                {s.name_english || s.name_arabic || s.name}
              </option>
            ))}
          </select>
          <button
            data-testid="supplier-statement-filter-toggle"
            onClick={() => setShowFilter(!showFilter)}
            className="border rounded-lg px-3 py-2 bg-white hover:bg-gray-50 text-sm flex items-center gap-1"
          >
            <Filter className="w-4 h-4" /> {dateFrom || dateTo ? 'Filtered' : 'Date Range'}
          </button>
          <button
            data-testid="supplier-statement-print"
            onClick={() => window.print()}
            className="border rounded-lg px-3 py-2 bg-white hover:bg-gray-50 text-sm flex items-center gap-1"
          >
            <Printer className="w-4 h-4" /> Print
          </button>
          <button
            data-testid="supplier-statement-pdf"
            onClick={downloadPDF}
            className="bg-blue-600 text-white rounded-lg px-3 py-2 text-sm hover:bg-blue-700 flex items-center gap-1"
          >
            <Download className="w-4 h-4" /> PDF
          </button>
        </div>
      </div>

      {showFilter && (
        <div className="bg-white border rounded-lg p-4 flex items-end gap-3 flex-wrap" data-testid="supplier-statement-filter-panel">
          <div>
            <label className="block text-xs text-gray-500 mb-1">From</label>
            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
                   className="border rounded px-2 py-1 text-sm" data-testid="supplier-statement-date-from" />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">To</label>
            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
                   className="border rounded px-2 py-1 text-sm" data-testid="supplier-statement-date-to" />
          </div>
          <button
            data-testid="supplier-statement-clear-dates"
            onClick={() => { setDateFrom(''); setDateTo(''); }}
            className="text-xs text-gray-500 hover:text-gray-800 underline"
          >
            Clear
          </button>
        </div>
      )}

      {!statement && (
        <div className="p-12 text-center text-gray-500 border rounded-lg bg-white" data-testid="supplier-statement-empty">
          No supplier selected.
        </div>
      )}

      {statement && (
        <>
          <div className="bg-white border rounded-xl p-5 shadow-sm" data-testid="supplier-statement-header">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <div className="text-xs uppercase text-gray-400 tracking-wide">Supplier</div>
                <div className="text-lg font-bold text-gray-900 mt-1">{p.name}</div>
                {p.name_ar && <div className="text-sm text-gray-600" dir="rtl">{p.name_ar}</div>}
                <div className="text-xs text-gray-500 mt-1">Code: <span className="font-mono">{p.code || '—'}</span></div>
                {p.vat_number && <div className="text-xs text-gray-500">VAT: <span className="font-mono">{p.vat_number}</span></div>}
              </div>
              <div>
                <div className="text-xs uppercase text-gray-400 tracking-wide">Period</div>
                <div className="mt-1 text-sm">
                  <Calendar className="inline w-4 h-4 mr-1 text-gray-400" />
                  {statement.date_from || 'Beginning'} <span className="text-gray-400 mx-1">→</span> {statement.date_to || 'Today'}
                </div>
                <div className="text-xs text-gray-500 mt-1">As of: {statement.as_of}</div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <SummaryTile label="Opening"      value={t.opening_balance}  accent="gray" />
                <SummaryTile label="Bills"        value={t.total_billed}     accent="blue" />
                <SummaryTile label="Payments"     value={t.total_paid}       accent="green" />
                <SummaryTile label="Debit Notes"  value={t.total_debit_notes} accent="amber" />
                <SummaryTile label="Closing"      value={t.closing_balance}  accent="blue-dark" wide />
              </div>
              {t.pending_count > 0 && (
                <div
                  className="mt-3 rounded-lg border border-yellow-300 bg-yellow-50 p-2 text-xs text-yellow-900 flex items-center justify-between"
                  data-testid="supplier-statement-pending-kpi"
                >
                  <span>
                    <strong>{t.pending_count}</strong> دَفعة بانتظار الاعتماد
                  </span>
                  <span className="font-mono">{fmt(t.pending_total)} SAR</span>
                </div>
              )}
            </div>
          </div>

          <div className="bg-white border rounded-xl p-4 shadow-sm" data-testid="supplier-statement-aging">
            <div className="text-xs uppercase text-gray-400 tracking-wide mb-2">Aging Analysis (as of {statement.as_of})</div>
            <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
              <AgingTile label="Current"  value={a.current} />
              <AgingTile label="1-30 d"   value={a.d_1_30} />
              <AgingTile label="31-60 d"  value={a.d_31_60} />
              <AgingTile label="61-90 d"  value={a.d_61_90} />
              <AgingTile label="91-120 d" value={a.d_91_120} />
              <AgingTile label="120+ d"   value={a.d_120_plus} danger />
            </div>
          </div>

          <div className="bg-white border rounded-xl shadow-sm overflow-hidden" data-testid="supplier-statement-table">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-600 text-xs uppercase tracking-wide">
                <tr>
                  <th className="text-left  px-4 py-2">Date</th>
                  <th className="text-left  px-4 py-2">Reference</th>
                  <th className="text-left  px-4 py-2">Type</th>
                  <th className="text-left  px-4 py-2">Description</th>
                  <th className="text-right px-4 py-2">Debit</th>
                  <th className="text-right px-4 py-2">Credit</th>
                  <th className="text-right px-4 py-2">Balance</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-t bg-gray-50/40" data-testid="supplier-statement-opening-row">
                  <td className="px-4 py-2 text-gray-500" colSpan={6}>Opening Balance</td>
                  <td className="px-4 py-2 text-right font-semibold">{fmt(statement.opening_balance)}</td>
                </tr>
                {statement.entries.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-gray-400">
                      No transactions in this period.
                    </td>
                  </tr>
                )}
                {statement.entries.map((e, i) => {
                  const tl = TYPE_LABEL[e.type] || { en: e.type, color: 'text-gray-700 bg-gray-100' };
                  const pendingClasses = e.pending
                    ? 'opacity-60 italic bg-yellow-50/40 hover:bg-yellow-50/60'
                    : 'hover:bg-blue-50/30';
                  return (
                    <tr
                      key={i}
                      className={`border-t ${pendingClasses}`}
                      data-testid={`supplier-statement-row-${i}`}
                    >
                      <td className="px-4 py-2 text-gray-700">{e.date}</td>
                      <td className="px-4 py-2 font-mono text-xs text-blue-700">{e.reference || '—'}</td>
                      <td className="px-4 py-2">
                        <div className="flex items-center gap-1">
                          <span className={`px-2 py-0.5 rounded text-xs ${tl.color}`}>{tl.en}</span>
                          {e.pending && (
                            <span
                              className="px-2 py-0.5 rounded text-[10px] font-semibold bg-yellow-100 text-yellow-800 border border-yellow-300"
                              data-testid={`supplier-statement-pending-badge-${i}`}
                              title="هذه الدفعة لم تُعتَمَد بعد ولا تؤثر على الرصيد المحاسبي"
                            >
                              بانتظار الاعتماد
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-2 text-gray-600 max-w-md truncate" title={e.description}>{e.description || '—'}</td>
                      <td className="px-4 py-2 text-right tabular-nums text-green-700">{e.debit ? fmt(e.debit) : ''}</td>
                      <td className="px-4 py-2 text-right tabular-nums text-blue-700">{e.credit ? fmt(e.credit) : ''}</td>
                      <td className="px-4 py-2 text-right tabular-nums font-semibold">
                        {e.pending ? '—' : fmt(e.balance)}
                      </td>
                    </tr>
                  );
                })}
                <tr className="border-t bg-blue-50" data-testid="supplier-statement-closing-row">
                  <td className="px-4 py-2 font-bold text-blue-700" colSpan={4}>Closing Balance</td>
                  <td className="px-4 py-2 text-right tabular-nums font-bold">{fmt(statement.entries.filter((e) => !e.pending).reduce((s, e) => s + (e.debit || 0), 0))}</td>
                  <td className="px-4 py-2 text-right tabular-nums font-bold">{fmt(statement.entries.filter((e) => !e.pending).reduce((s, e) => s + (e.credit || 0), 0))}</td>
                  <td className="px-4 py-2 text-right tabular-nums font-bold text-blue-700">{fmt(t.closing_balance)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

const accentStyles = {
  gray:      'bg-gray-50    text-gray-700  border-gray-200',
  blue:      'bg-blue-50    text-blue-700  border-blue-200',
  green:     'bg-green-50   text-green-700 border-green-200',
  amber:     'bg-amber-50   text-amber-700 border-amber-200',
  'blue-dark': 'bg-blue-700 text-white     border-blue-700',
};

const SummaryTile = ({ label, value, accent = 'gray', wide = false }) => (
  <div className={`border rounded-lg p-2 ${accentStyles[accent]} ${wide ? 'col-span-2' : ''}`} data-testid={`supplier-statement-tile-${label.toLowerCase().replace(/\s+/g, '-')}`}>
    <div className="text-[10px] uppercase tracking-wide opacity-70">{label}</div>
    <div className="text-base font-bold tabular-nums">{fmt(value)} <span className="text-xs opacity-60">SAR</span></div>
  </div>
);

const AgingTile = ({ label, value, danger = false }) => (
  <div className={`border rounded-lg p-2 text-center ${danger && value > 0 ? 'bg-red-50 border-red-300 text-red-700' : 'bg-gray-50'}`}>
    <div className="text-xs text-gray-500">{label}</div>
    <div className="font-bold tabular-nums">{fmt(value)}</div>
  </div>
);

export default SupplierStatement;
